import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowTopRightOnSquareIcon,
  LinkIcon,
  CalendarIcon,
  UserIcon,
  BuildingOfficeIcon,
  BanknotesIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  CardBody,
  Badge,
  Spinner,
  StatusBadge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
} from '@/components/ui';
import { paymentLinkApi, getErrorMessage } from '@/lib/api';
import type { PaymentLink, PaymentLinkItem, RecurringSchedule, Transaction, PaymentType, RecurringFrequency } from '@/types';

interface PaymentLinkDetail extends PaymentLink {
  lineItems?: PaymentLinkItem[];
  recurringSchedules?: RecurringSchedule[];
  transactions?: Transaction[];
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  sendEmailOnCreate?: boolean;
  sendReminderEmails?: boolean;
}

export function PaymentLinkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    details: true,
    customer: true,
    property: true,
    lineItems: true,
    recurringSchedule: true,
    transactions: true,
  });
  const [cancelModal, setCancelModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    paymentType: '' as PaymentType | '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    propertyLocation: '',
    unitNumber: '',
    contractNumber: '',
    expiresAt: '',
    sendReminderEmails: true,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['payment-link', id],
    queryFn: async () => {
      const response = await paymentLinkApi.getById(id!);
      return response.data.data as PaymentLinkDetail;
    },
    enabled: !!id,
  });

  const cancelMutation = useMutation({
    mutationFn: () => paymentLinkApi.cancel(id!),
    onSuccess: () => {
      toast.success('Payment link cancelled');
      queryClient.invalidateQueries({ queryKey: ['payment-link', id] });
      setCancelModal(false);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: typeof editForm) => {
      const payload: Record<string, unknown> = {};
      if (data.title) payload.title = data.title;
      if (data.description !== undefined) payload.description = data.description;
      if (data.paymentType) payload.paymentType = data.paymentType;
      if (data.customerName !== undefined) payload.customerName = data.customerName || null;
      if (data.customerEmail !== undefined) payload.customerEmail = data.customerEmail || null;
      if (data.customerPhone !== undefined) payload.customerPhone = data.customerPhone || null;
      if (data.propertyLocation !== undefined) payload.propertyLocation = data.propertyLocation || null;
      if (data.unitNumber !== undefined) payload.unitNumber = data.unitNumber || null;
      if (data.contractNumber !== undefined) payload.contractNumber = data.contractNumber || null;
      if (data.expiresAt !== undefined) payload.expiresAt = data.expiresAt || null;
      payload.sendReminderEmails = data.sendReminderEmails;
      return paymentLinkApi.update(id!, payload);
    },
    onSuccess: () => {
      toast.success('Payment link updated');
      queryClient.invalidateQueries({ queryKey: ['payment-link', id] });
      setEditModal(false);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  const openEditModal = () => {
    if (data) {
      setEditForm({
        title: data.title || '',
        description: data.description || '',
        paymentType: data.paymentType || '',
        customerName: data.customerName || '',
        customerEmail: data.customerEmail || '',
        customerPhone: data.customerPhone || '',
        propertyLocation: data.propertyLocation || '',
        unitNumber: data.unitNumber || '',
        contractNumber: data.contractNumber || '',
        expiresAt: data.expiresAt ? format(new Date(data.expiresAt), "yyyy-MM-dd'T'HH:mm") : '',
        sendReminderEmails: data.sendReminderEmails ?? true,
      });
      setEditModal(true);
    }
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(editForm);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const copyPaymentLink = () => {
    if (!data) return;
    const metadata = data.metadata as { adyenLinkUrl?: string } | null;
    const url = metadata?.adyenLinkUrl || `${window.location.origin}/pay/${data.linkCode}`;
    copyToClipboard(url, 'Payment link');
  };

  const formatCurrency = (amount: number | string, currency = 'PHP') => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency,
    }).format(numAmount);
  };

  const getPaymentTypeLabel = (type: PaymentType) => {
    const labels: Record<PaymentType, string> = {
      RESERVATION_FEE: 'Reservation Fee',
      DOWN_PAYMENT: 'Down Payment',
      MONTHLY_AMORTIZATION: 'Monthly Amortization',
      SPOT_CASH: 'Spot Cash',
      BALANCE_PAYMENT: 'Balance Payment',
      OTHER: 'Other',
    };
    return labels[type];
  };

  const getFrequencyLabel = (freq?: RecurringFrequency) => {
    if (!freq) return '-';
    const labels: Record<RecurringFrequency, string> = {
      WEEKLY: 'Weekly',
      MONTHLY: 'Monthly',
      QUARTERLY: 'Quarterly',
      ANNUALLY: 'Annually',
    };
    return labels[freq];
  };

  const getScheduleStatusIcon = (status: string, dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);

    if (status === 'PAID') {
      return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
    }
    if (due < now) {
      return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
    }
    return <ClockIcon className="w-5 h-5 text-gray-400" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-error">{error ? getErrorMessage(error) : 'Payment link not found'}</p>
        <Button variant="outline" onClick={() => navigate('/admin/payment-links')}>
          Back to Payment Links
        </Button>
      </div>
    );
  }

  const link = data;
  const hasLineItems = link.lineItems && link.lineItems.length > 0;
  const hasRecurringSchedules = link.recurringSchedules && link.recurringSchedules.length > 0;
  const hasTransactions = link.transactions && link.transactions.length > 0;
  const amountDue = Number(link.amount) - Number(link.amountPaid);
  const paymentProgress = Number(link.amount) > 0 ? (Number(link.amountPaid) / Number(link.amount)) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/payment-links')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{link.title}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {link.linkCode} · Created {format(new Date(link.createdAt), 'MMM d, yyyy')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={copyPaymentLink}>
            <DocumentDuplicateIcon className="w-4 h-4 mr-2" />
            Copy Link
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(`/pay/${link.linkCode}`, '_blank')}
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-2" />
            Open Payment Page
          </Button>
          {(link.status === 'ACTIVE' || link.status === 'DRAFT') && (
            <Button variant="outline" onClick={openEditModal}>
              <PencilIcon className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          {link.status === 'ACTIVE' && (
            <Button variant="danger" onClick={() => setCancelModal(true)}>
              Cancel Link
            </Button>
          )}
        </div>
      </div>

      {/* Status and Amount Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card>
          <CardBody className="p-6">
            <p className="text-sm text-gray-500 mb-2">Status</p>
            <StatusBadge status={link.status} />
          </CardBody>
        </Card>

        <Card>
          <CardBody className="p-6">
            <p className="text-sm text-gray-500 mb-2">Total Amount</p>
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(link.amount, link.currency)}
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="p-6">
            <p className="text-sm text-gray-500 mb-2">Amount Paid</p>
            <p className="text-xl font-bold text-green-600">
              {formatCurrency(link.amountPaid, link.currency)}
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="p-6">
            <p className="text-sm text-gray-500 mb-2">Amount Due</p>
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(amountDue, link.currency)}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Payment Progress */}
      {link.allowPartialPayment && (
        <Card>
          <CardBody className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">Payment Progress</p>
              <p className="text-sm text-gray-500">{paymentProgress.toFixed(1)}%</p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-green-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(paymentProgress, 100)}%` }}
              />
            </div>
          </CardBody>
        </Card>
      )}

      {/* Link Code and URL */}
      <Card>
        <CardBody className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary-50 rounded-lg">
                <LinkIcon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Payment Link Code</p>
                <p className="font-mono text-lg font-medium text-gray-900">{link.linkCode}</p>
              </div>
            </div>
            <button
              onClick={() => copyToClipboard(link.linkCode, 'Link code')}
              className="p-2 text-gray-400 hover:text-gray-600 rounded"
            >
              <DocumentDuplicateIcon className="w-5 h-5" />
            </button>
          </div>
        </CardBody>
      </Card>

      {/* Payment Details */}
      <Card>
        <CardBody className="p-0">
          <button
            onClick={() => toggleSection('details')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <BanknotesIcon className="w-5 h-5 text-gray-400" />
              <h3 className="font-semibold text-gray-900">Payment Details</h3>
            </div>
            {expandedSections.details ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSections.details && (
            <div className="px-6 pb-6 border-t border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Payment Type</p>
                  <p className="text-sm font-medium text-gray-900">
                    {getPaymentTypeLabel(link.paymentType)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Currency</p>
                  <p className="text-sm font-medium text-gray-900">{link.currency}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Partial Payment</p>
                  <p className="text-sm font-medium text-gray-900">
                    {link.allowPartialPayment ? 'Allowed' : 'Not Allowed'}
                  </p>
                </div>
                {link.minimumAmount && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Minimum Amount</p>
                    <p className="text-sm font-medium text-gray-900">
                      {formatCurrency(link.minimumAmount, link.currency)}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Recurring Payment</p>
                  <p className="text-sm font-medium text-gray-900">
                    {link.isRecurring ? 'Yes' : 'No'}
                  </p>
                </div>
                {link.isRecurring && (
                  <>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Frequency</p>
                      <p className="text-sm font-medium text-gray-900">
                        {getFrequencyLabel(link.recurringFrequency)}
                      </p>
                    </div>
                    {link.recurringCount && (
                      <div>
                        <p className="text-sm text-gray-500 mb-1">Number of Payments</p>
                        <p className="text-sm font-medium text-gray-900">
                          {link.recurringCompleted} / {link.recurringCount}
                        </p>
                      </div>
                    )}
                  </>
                )}
                {link.expiresAt && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Expires At</p>
                    <p className="text-sm font-medium text-gray-900">
                      {format(new Date(link.expiresAt), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                )}
                {link.description && (
                  <div className="col-span-full">
                    <p className="text-sm text-gray-500 mb-1">Description</p>
                    <p className="text-sm text-gray-900">{link.description}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Customer Information */}
      <Card>
        <CardBody className="p-0">
          <button
            onClick={() => toggleSection('customer')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <UserIcon className="w-5 h-5 text-gray-400" />
              <h3 className="font-semibold text-gray-900">Customer Information</h3>
            </div>
            {expandedSections.customer ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSections.customer && (
            <div className="px-6 pb-6 border-t border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Name</p>
                  <p className="text-sm font-medium text-gray-900">
                    {link.customerName || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="text-sm font-medium text-gray-900">
                    {link.customerEmail || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Phone</p>
                  <p className="text-sm font-medium text-gray-900">
                    {link.customerPhone || '-'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Property Information */}
      <Card>
        <CardBody className="p-0">
          <button
            onClick={() => toggleSection('property')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
              <h3 className="font-semibold text-gray-900">Property Information</h3>
            </div>
            {expandedSections.property ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSections.property && (
            <div className="px-6 pb-6 border-t border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Location</p>
                  <p className="text-sm font-medium text-gray-900">
                    {link.propertyLocation || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Unit Number</p>
                  <p className="text-sm font-medium text-gray-900">
                    {link.unitNumber || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Contract Number</p>
                  <p className="text-sm font-medium text-gray-900">
                    {link.contractNumber || '-'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Line Items */}
      {hasLineItems && (
        <Card>
          <CardBody className="p-0">
            <button
              onClick={() => toggleSection('lineItems')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
            >
              <h3 className="font-semibold text-gray-900">
                Line Items ({link.lineItems!.length})
              </h3>
              {expandedSections.lineItems ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.lineItems && (
              <div className="px-6 pb-4 border-t border-gray-100">
                <div className="space-y-4 mt-4">
                  {link.lineItems!.map((item, index) => {
                    const itemWithRecurring = item as PaymentLinkItem & {
                      isRecurring?: boolean;
                      recurringFrequency?: RecurringFrequency;
                      recurringStartDate?: string;
                      recurringEndDate?: string;
                      recurringCount?: number;
                      recurringCompleted?: number;
                    };

                    return (
                      <div
                        key={item.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                      >
                        {/* Item Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
                              {index + 1}
                            </span>
                            <div>
                              <h4 className="font-medium text-gray-900">{item.description}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="gray" className="text-xs">
                                  {getPaymentTypeLabel(item.paymentType)}
                                </Badge>
                                {itemWithRecurring.isRecurring ? (
                                  <Badge variant="info" className="text-xs">
                                    Recurring ({getFrequencyLabel(itemWithRecurring.recurringFrequency)})
                                  </Badge>
                                ) : (
                                  <Badge variant="gray" className="text-xs">
                                    One-time
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-gray-900">
                              {formatCurrency(item.amount, link.currency)}
                            </p>
                            {item.quantity > 1 && (
                              <p className="text-xs text-gray-500">
                                {item.quantity} × {formatCurrency(item.unitPrice, link.currency)}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Item Details Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-gray-100">
                          <div>
                            <p className="text-xs text-gray-500">Quantity</p>
                            <p className="text-sm font-medium text-gray-900">{item.quantity}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Unit Price</p>
                            <p className="text-sm font-medium text-gray-900">
                              {formatCurrency(item.unitPrice, link.currency)}
                            </p>
                          </div>
                          {item.referenceNumber && (
                            <div>
                              <p className="text-xs text-gray-500">Reference #</p>
                              <p className="text-sm font-medium text-gray-900">{item.referenceNumber}</p>
                            </div>
                          )}
                          {item.referenceDate && (
                            <div>
                              <p className="text-xs text-gray-500">Reference Date</p>
                              <p className="text-sm font-medium text-gray-900">
                                {format(new Date(item.referenceDate), 'MMM d, yyyy')}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Recurring Details */}
                        {itemWithRecurring.isRecurring && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                              Recurring Schedule
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <p className="text-xs text-gray-500">Frequency</p>
                                <p className="text-sm font-medium text-gray-900">
                                  {getFrequencyLabel(itemWithRecurring.recurringFrequency)}
                                </p>
                              </div>
                              {itemWithRecurring.recurringStartDate && (
                                <div>
                                  <p className="text-xs text-gray-500">Start Date</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {format(new Date(itemWithRecurring.recurringStartDate), 'MMM d, yyyy')}
                                  </p>
                                </div>
                              )}
                              {itemWithRecurring.recurringEndDate && (
                                <div>
                                  <p className="text-xs text-gray-500">End Date</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {format(new Date(itemWithRecurring.recurringEndDate), 'MMM d, yyyy')}
                                  </p>
                                </div>
                              )}
                              {itemWithRecurring.recurringCount && (
                                <div>
                                  <p className="text-xs text-gray-500">Payments</p>
                                  <p className="text-sm font-medium text-gray-900">
                                    {itemWithRecurring.recurringCompleted || 0} / {itemWithRecurring.recurringCount}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Total */}
                <div className="flex justify-end mt-4 pt-4 border-t border-gray-200">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(link.amount, link.currency)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Recurring Schedule */}
      {hasRecurringSchedules && (
        <Card>
          <CardBody className="p-0">
            <button
              onClick={() => toggleSection('recurringSchedule')}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <CalendarIcon className="w-5 h-5 text-gray-400" />
                <h3 className="font-semibold text-gray-900">
                  Payment Schedule ({link.recurringSchedules!.length})
                </h3>
              </div>
              {expandedSections.recurringSchedule ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {expandedSections.recurringSchedule && (
              <div className="px-6 pb-4 border-t border-gray-100">
                <div className="overflow-x-auto">
                  <table className="w-full mt-4">
                    <thead>
                      <tr className="text-left text-sm text-gray-500 border-b border-gray-100">
                        <th className="pb-3 font-medium">#</th>
                        <th className="pb-3 font-medium">Due Date</th>
                        <th className="pb-3 font-medium text-right">Amount</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Paid At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {link.recurringSchedules!.map((schedule) => {
                        const now = new Date();
                        const dueDate = new Date(schedule.dueDate);
                        const isOverdue = schedule.status !== 'PAID' && dueDate < now;

                        return (
                          <tr key={schedule.id} className={isOverdue ? 'bg-red-50' : ''}>
                            <td className="py-3 text-sm text-gray-900">
                              {schedule.scheduleNumber}
                            </td>
                            <td className="py-3 text-sm text-gray-900">
                              <div className="flex items-center gap-2">
                                {getScheduleStatusIcon(schedule.status, schedule.dueDate)}
                                {format(dueDate, 'MMM d, yyyy')}
                              </div>
                            </td>
                            <td className="py-3 text-sm font-medium text-gray-900 text-right">
                              {formatCurrency(schedule.amount, link.currency)}
                            </td>
                            <td className="py-3">
                              <Badge
                                variant={
                                  schedule.status === 'PAID'
                                    ? 'success'
                                    : isOverdue
                                    ? 'error'
                                    : 'gray'
                                }
                              >
                                {schedule.status === 'PAID'
                                  ? 'Paid'
                                  : isOverdue
                                  ? 'Overdue'
                                  : 'Upcoming'}
                              </Badge>
                            </td>
                            <td className="py-3 text-sm text-gray-500">
                              {schedule.paidAt
                                ? format(new Date(schedule.paidAt), 'MMM d, yyyy')
                                : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Transactions */}
      <Card>
        <CardBody className="p-0">
          <button
            onClick={() => toggleSection('transactions')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
          >
            <h3 className="font-semibold text-gray-900">
              Transactions {hasTransactions ? `(${link.transactions!.length})` : ''}
            </h3>
            {expandedSections.transactions ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {expandedSections.transactions && (
            <div className="px-6 pb-4 border-t border-gray-100">
              {hasTransactions ? (
                <div className="overflow-x-auto">
                  <table className="w-full mt-4">
                    <thead>
                      <tr className="text-left text-sm text-gray-500 border-b border-gray-100">
                        <th className="pb-3 font-medium">Reference</th>
                        <th className="pb-3 font-medium">Date</th>
                        <th className="pb-3 font-medium text-right">Amount</th>
                        <th className="pb-3 font-medium">Method</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {link.transactions!.map((tx) => (
                        <tr key={tx.id}>
                          <td className="py-3">
                            <p className="text-sm font-mono text-gray-900">
                              {tx.transactionRef}
                            </p>
                            {tx.pspReference && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                PSP: {tx.pspReference}
                              </p>
                            )}
                          </td>
                          <td className="py-3 text-sm text-gray-500">
                            {format(new Date(tx.createdAt), 'MMM d, yyyy h:mm a')}
                          </td>
                          <td className="py-3 text-sm font-medium text-gray-900 text-right">
                            {formatCurrency(tx.amount, tx.currency)}
                          </td>
                          <td className="py-3 text-sm text-gray-900">
                            {tx.paymentMethod || '-'}
                            {tx.cardLastFour && (
                              <span className="text-gray-500"> •••• {tx.cardLastFour}</span>
                            )}
                          </td>
                          <td className="py-3">
                            <Badge
                              variant={
                                ['AUTHORIZED', 'CAPTURED', 'SETTLED'].includes(tx.status)
                                  ? 'success'
                                  : tx.status === 'PENDING'
                                  ? 'warning'
                                  : tx.status === 'REFUNDED'
                                  ? 'info'
                                  : 'error'
                              }
                            >
                              {tx.status}
                            </Badge>
                          </td>
                          <td className="py-3 text-right">
                            <Link
                              to={`/admin/transactions/${tx.id}`}
                              className="text-sm text-primary hover:underline"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <p>No transactions yet</p>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Metadata */}
      <Card>
        <CardBody className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Additional Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Created By</p>
              <p className="text-sm font-medium text-gray-900">
                {link.createdBy
                  ? `${link.createdBy.firstName} ${link.createdBy.lastName}`
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Created At</p>
              <p className="text-sm font-medium text-gray-900">
                {format(new Date(link.createdAt), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Last Updated</p>
              <p className="text-sm font-medium text-gray-900">
                {format(new Date(link.updatedAt), 'MMM d, yyyy h:mm a')}
              </p>
            </div>
            {link.paidAt && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Paid At</p>
                <p className="text-sm font-medium text-gray-900">
                  {format(new Date(link.paidAt), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500 mb-1">Email on Create</p>
              <p className="text-sm font-medium text-gray-900">
                {link.sendEmailOnCreate ? 'Yes' : 'No'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Reminder Emails</p>
              <p className="text-sm font-medium text-gray-900">
                {link.sendReminderEmails ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Cancel Modal */}
      <Modal isOpen={cancelModal} onClose={() => setCancelModal(false)} size="sm">
        <ModalHeader onClose={() => setCancelModal(false)}>Cancel Payment Link</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600">
            Are you sure you want to cancel this payment link? This action cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setCancelModal(false)}>
            Keep Link
          </Button>
          <Button
            variant="danger"
            isLoading={cancelMutation.isPending}
            onClick={() => cancelMutation.mutate()}
          >
            Cancel Link
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editModal} onClose={() => setEditModal(false)} size="lg">
        <ModalHeader onClose={() => setEditModal(false)}>Edit Payment Link</ModalHeader>
        <form onSubmit={handleEditSubmit}>
          <ModalBody className="space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Basic Details */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Basic Details</h4>
              <div className="space-y-4">
                <Input
                  label="Title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
                <Select
                  label="Payment Type"
                  value={editForm.paymentType}
                  onChange={(e) => setEditForm({ ...editForm, paymentType: e.target.value as PaymentType })}
                  options={[
                    { value: 'RESERVATION_FEE', label: 'Reservation Fee' },
                    { value: 'DOWN_PAYMENT', label: 'Down Payment' },
                    { value: 'MONTHLY_AMORTIZATION', label: 'Monthly Amortization' },
                    { value: 'SPOT_CASH', label: 'Spot Cash' },
                    { value: 'BALANCE_PAYMENT', label: 'Balance Payment' },
                    { value: 'OTHER', label: 'Other' },
                  ]}
                />
                <Input
                  label="Expiry Date"
                  type="datetime-local"
                  value={editForm.expiresAt}
                  onChange={(e) => setEditForm({ ...editForm, expiresAt: e.target.value })}
                />
              </div>
            </div>

            {/* Customer Information */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Customer Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Customer Name"
                  value={editForm.customerName}
                  onChange={(e) => setEditForm({ ...editForm, customerName: e.target.value })}
                />
                <Input
                  label="Customer Email"
                  type="email"
                  value={editForm.customerEmail}
                  onChange={(e) => setEditForm({ ...editForm, customerEmail: e.target.value })}
                />
                <Input
                  label="Customer Phone"
                  value={editForm.customerPhone}
                  onChange={(e) => setEditForm({ ...editForm, customerPhone: e.target.value })}
                />
              </div>
            </div>

            {/* Property Information */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Property Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Property Location"
                  value={editForm.propertyLocation}
                  onChange={(e) => setEditForm({ ...editForm, propertyLocation: e.target.value })}
                  placeholder="Select location"
                  options={[
                    { value: 'Niseko', label: 'Niseko' },
                    { value: 'Davao', label: 'Davao' },
                    { value: 'Madrid', label: 'Madrid' },
                    { value: 'Libis', label: 'Libis' },
                  ]}
                />
                <Input
                  label="Unit Number"
                  value={editForm.unitNumber}
                  onChange={(e) => setEditForm({ ...editForm, unitNumber: e.target.value })}
                />
                <Input
                  label="Contract Number"
                  value={editForm.contractNumber}
                  onChange={(e) => setEditForm({ ...editForm, contractNumber: e.target.value })}
                />
              </div>
            </div>

            {/* Settings */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Settings</h4>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.sendReminderEmails}
                  onChange={(e) => setEditForm({ ...editForm, sendReminderEmails: e.target.checked })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Send reminder emails</span>
              </label>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={() => setEditModal(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Save Changes
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}
